import { authService } from '@/services/auth/authService';

const STORAGE_PREFIX = 'nexo.recentRecords';
const MAX_ENTRIES = 20;

type Listener = () => void;

export interface RecentRecord {
  module: string;
  entityType: string;
  entityId: string;
  /** Business identity, e.g. 'ORD-001002'. Absent until the record has been read once. */
  label?: string;
  path: string;
}

/**
 * MRU store of records the user has opened, newest first.
 *
 * Keyed per tenant: the same browser can be used against several tenants, and a history
 * listing another tenant's order numbers would leak data across a boundary the rest of
 * the app enforces. Switching tenant therefore switches history rather than merging it.
 */
function storageKey(): string {
  return `${STORAGE_PREFIX}.${authService.getTenantId() ?? 'host'}`;
}

function safeRead(): RecentRecord[] {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (x): x is RecentRecord =>
        !!x && typeof x.entityId === 'string' && typeof x.entityType === 'string' && typeof x.path === 'string',
    );
  } catch {
    return [];
  }
}

function safeWrite(records: RecentRecord[]): void {
  try {
    localStorage.setItem(storageKey(), JSON.stringify(records));
  } catch {
    // localStorage may be unavailable (private mode, quota). Fail silently.
  }
}

// In-memory snapshot so useSyncExternalStore returns a stable reference between renders
// unless the data actually changed — an unstable one resets the palette's selected index.
let snapshot: RecentRecord[] = safeRead();
let loadedForKey = storageKey();
const listeners = new Set<Listener>();

function emit() {
  for (const listener of listeners) listener();
}

export const recentRecordsStore = {
  /** Read the current list. Returns a stable reference between mutations. */
  getRecent(): RecentRecord[] {
    // Tenant can change without a reload (tenant switcher), so re-read on a key change.
    const key = storageKey();
    if (key !== loadedForKey) {
      loadedForKey = key;
      snapshot = safeRead();
    }
    return snapshot;
  },

  /**
   * Move `record` to the front, deduplicating on entity + id. Re-recording the same record
   * with a label it previously lacked upgrades the entry in place, which is what happens
   * when the page's data lands a moment after navigation.
   */
  record(record: RecentRecord): void {
    const current = recentRecordsStore.getRecent();
    const isSame = (x: RecentRecord) =>
      x.entityId === record.entityId && x.entityType === record.entityType;

    const existing = current.find(isSame);
    // Nothing changed — do not emit, or every render of a record page would notify.
    if (existing && current[0] === existing && existing.label === record.label) return;

    const merged: RecentRecord = { ...record, label: record.label ?? existing?.label };
    snapshot = [merged, ...current.filter((x) => !isSame(x))].slice(0, MAX_ENTRIES);
    safeWrite(snapshot);
    emit();
  },

  clear(): void {
    if (recentRecordsStore.getRecent().length === 0) return;
    snapshot = [];
    safeWrite(snapshot);
    emit();
  },

  subscribe(listener: Listener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
